import axios from 'axios';

const API_BASE_URL = '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

// 请求拦截器：添加门店ID、用户ID和角色到请求头
apiClient.interceptors.request.use(config => {
  const user = JSON.parse(sessionStorage.getItem('health_user') || '{}');
  if (user.storeId) {
    config.headers['x-store-id'] = user.storeId;
  }
  if (user.id) {
    config.headers['x-user-id'] = user.id;
  }
  if (user.role) {
    config.headers['x-user-role'] = user.role;
  }
  return config;
});

// Login - 支持带storeId登录
export const login = (username, password, storeId) => {
  if (storeId && storeId !== 'admin') {
    return apiClient.post('/login', { username, password, storeId: parseInt(storeId) });
  }
  return apiClient.post('/login', { username, password });
};

// Dashboard APIs
export const getDashboardStats = (storeId) => apiClient.get('/dashboard/stats', { params: { store_id: storeId } });
export const getDashboardTrend = () => apiClient.get('/dashboard/trend');
export const getMonthlyData = () => apiClient.get('/dashboard/monthly');
export const getRecentServices = () => apiClient.get('/dashboard/recent-services');
export const getRevenueReport = () => apiClient.get('/dashboard/revenue-report');
export const getServiceRevenueRanking = () => apiClient.get('/dashboard/service-revenue-ranking');
export const getFinancialSummary = () => apiClient.get('/dashboard/financial-summary');

// Customer APIs
export const getCustomers = (params) => apiClient.get('/customers', { params });
export const getCustomer = (id) => apiClient.get(`/customers/${id}`);
export const createCustomer = (data) => apiClient.post('/customers', data);
export const updateCustomer = (id, data) => apiClient.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => apiClient.delete(`/customers/${id}`);

// 客户体型记录和打卡统计
export const getCustomerBodyRecords = (id, range) => apiClient.get(`/customers/${id}/body-records`, { params: { range } });
export const getCustomerHabitStats = (id) => apiClient.get(`/customers/${id}/habit-stats`);

// 客户身体成分API
export const getCustomerBodyComposition = (customerId) => apiClient.get(`/customers/${customerId}/body-composition`);
export const saveCustomerBodyComposition = (customerId, data) => apiClient.post(`/customers/${customerId}/body-composition`, data);

// 客户打卡记录
export const getCustomerCheckinRecords = (id, dateRange, customStartDate, customEndDate) => apiClient.get(`/customers/${id}/checkin-records`, { params: { dateRange, custom_start_date: customStartDate, custom_end_date: customEndDate } });

// Service APIs
export const getServices = (params) => apiClient.get('/services', { params });
export const getProducts = () => apiClient.get('/dietary/products');
export const getService = (id) => apiClient.get(`/services/${id}`);
export const createService = (data) => apiClient.post('/services', data);
export const updateService = (id, data) => apiClient.put(`/services/${id}`, data);
export const deleteService = (id) => apiClient.delete(`/services/${id}`);

// Therapy Template APIs
export const getTherapyTemplates = (params) => apiClient.get('/therapy/templates', { params });
export const getTherapyTemplate = (id) => apiClient.get(`/therapy/templates/${id}`);

// Customer Recommendation APIs
export const getCustomerRecommendations = (customerId) => apiClient.get(`/customers/${customerId}/recommendations`);
export const createCustomerRecommendation = (customerId, data) => apiClient.post(`/customers/${customerId}/recommendations`, data);
export const deleteCustomerRecommendation = (customerId, serviceId) => apiClient.delete(`/customers/${customerId}/recommendations/${serviceId}`);

// Order APIs
export const getOrders = (params) => apiClient.get('/orders', { params });
export const getOrder = (id) => apiClient.get(`/orders/${id}`);
export const createOrder = (data) => apiClient.post('/orders', data);
export const updateOrder = (id, data) => apiClient.put(`/orders/${id}`, data);
export const updateOrderStatus = (id, status) => apiClient.patch(`/orders/${id}/status`, { status });
export const deleteOrder = (id) => apiClient.delete(`/orders/${id}`);
export const getOrderStatistics = () => apiClient.get('/orders/statistics');
export const requestRefund = (id, data) => apiClient.post(`/orders/${id}/refund`, data);
export const approveRefund = (id, action) => apiClient.patch(`/orders/${id}/refund`, { action });
export const getRefunds = (params) => apiClient.get('/orders/refunds', { params });
export const getRefundStatistics = () => apiClient.get('/orders/refund-statistics');

// Marketing APIs
export const getMarketingActivities = (params) => apiClient.get('/marketing/activities', { params });
export const getMarketingActivity = (id) => apiClient.get(`/marketing/activities/${id}`);
export const createMarketingActivity = (data) => apiClient.post('/marketing/activities', data);
export const updateMarketingActivity = (id, data) => apiClient.put(`/marketing/activities/${id}`, data);
export const deleteMarketingActivity = (id) => apiClient.delete(`/marketing/activities/${id}`);
export const getMarketingStatistics = () => apiClient.get('/marketing/statistics');

// User APIs
export const getUsers = () => apiClient.get('/users');
export const getUser = (id) => apiClient.get(`/users/${id}`);
export const getUserByPhone = (phone) => apiClient.get(`/users/by-phone/${phone}`);
export const createUser = (data) => apiClient.post('/users', data);
export const updateUser = (id, data) => apiClient.put(`/users/${id}`, data);
export const updateMyProfile = (data) => apiClient.patch('/users/profile', data);
export const deleteUser = (id) => apiClient.delete(`/users/${id}`);

// Checkin APIs（打卡审核）
export const getCheckins = (params) => apiClient.get('/checkins', { params });
export const getPendingCheckins = () => apiClient.get('/checkins/pending');
export const approveCheckin = (id) => apiClient.patch(`/checkins/${id}/approve`);
export const rejectCheckin = (id) => apiClient.patch(`/checkins/${id}/reject`);

// Store APIs（门店管理）
export const getStores = () => apiClient.get('/stores');
export const getStore = (id) => apiClient.get(`/stores/${id}`);
export const createStore = (data) => apiClient.post('/stores', data);
export const updateStore = (id, data) => apiClient.put(`/stores/${id}`, data);
export const deleteStore = (id) => apiClient.delete(`/stores/${id}`);

// Logs & Backup APIs
export const getOperationLogs = (params) => apiClient.get('/logs', { params });
export const getLogStatistics = () => apiClient.get('/logs/statistics');
export const getDatabaseInfo = () => apiClient.get('/database/info');
export const getSystemSettings = () => apiClient.get('/logs/settings');
export const updateSystemSettings = (key, value) => apiClient.put('/logs/settings', { key, value });
export const backupDatabase = () => apiClient.post('/backup');
export const getBackups = () => apiClient.get('/backups');
export const getBackup = (filename) => apiClient.get(`/backups/${filename}`, { responseType: 'blob' });
export const deleteBackup = (filename) => apiClient.delete(`/backups/${filename}`);
export const restoreDatabase = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post('/restore', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const cleanupLogs = (days, filter) => apiClient.delete('/logs/cleanup', { data: { days, filter } });

// Dietary Product APIs（膳食产品管理）
export const getDietaryProducts = (params) => apiClient.get('/dietary/products', { params });
export const getDietaryProduct = (id) => apiClient.get(`/dietary/products/${id}`);
export const getDietaryProductMeals = (productId, day) => apiClient.get(`/dietary/products/${productId}/meals`, { params: { day } });
export const createDietaryProduct = (data) => apiClient.post('/dietary/products', data);
export const updateDietaryProduct = (id, data) => apiClient.put(`/dietary/products/${id}`, data);
export const deleteDietaryProduct = (id) => apiClient.delete(`/dietary/products/${id}`);

// 保存营养数据（批量导入餐次）
export const saveDietaryProductMeals = (productId, meals) => apiClient.post(`/dietary/products/${productId}/meals`, { meals });

// Customer Dietary Recommendation APIs（客户膳食推荐）
export const getCustomerDietaryRecommendations = (customerId) => apiClient.get(`/customers/${customerId}/dietary-recommendations`);
export const createCustomerDietaryRecommendation = (customerId, data) => apiClient.post(`/customers/${customerId}/dietary-recommendations`, data);
export const deleteCustomerDietaryRecommendation = (customerId, productId) => apiClient.delete(`/customers/${customerId}/dietary-recommendations/${productId}`);

// Customer Purchased Products APIs（客户购买记录）
export const getCustomerPurchasedProducts = (customerId) => apiClient.get(`/dietary/purchased/${customerId}`);
export const createCustomerPurchasedProduct = (data) => apiClient.post('/dietary/purchased', data);
export const updateCustomerPurchasedProductStatus = (id, data) => apiClient.put(`/dietary/purchased/${id}/status`, data);
export const deleteCustomerPurchasedProduct = (id) => apiClient.delete(`/dietary/purchased/${id}`);

// Customer Meal Records APIs（客户膳食记录）
export const getCustomerMealRecords = (customerId, params) => apiClient.get(`/dietary/records/${customerId}`, { params });
export const createCustomerMealRecord = (data) => apiClient.post('/dietary/records', data);
export const exportLogsCSV = (params) => apiClient.get('/logs/export', { params, responseType: 'blob' });

// Customer Purchased Services APIs（客户购买的服务）
export const getCustomerPurchasedServices = (customerId) => apiClient.get(`/customers/${customerId}/purchased-services`);
export const createCustomerPurchasedService = (customerId, data) => apiClient.post(`/customers/${customerId}/purchased-services`, data);
export const updateCustomerPurchasedServiceStatus = (id, status) => apiClient.put(`/services/purchased/${id}/status`, { status });
export const deleteCustomerPurchasedService = (id) => apiClient.delete(`/services/purchased/${id}`);

// Service Records APIs（服务记录）
export const getServiceRecords = (purchasedServiceId) => apiClient.get(`/services/records/${purchasedServiceId}`);
export const createServiceRecord = (purchasedServiceId, data) => apiClient.post(`/services/records/${purchasedServiceId}`, data);

export default apiClient;